import { Permission, Role } from '@ekkogmbh/apisdk';
import { Add, AdminPanelSettingsOutlined, Close, Save } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { RolePermissionSelect } from '../RolePermissionSelect';

interface Props {
  role?: Role;
  /** All permissions selectable for this role (fetched once from the API). */
  availablePermissions: Permission[];
  closeHandler: () => void;
  saveHandler: (role: Partial<Role>) => void;
}

const SectionHeader = ({ title }: { title: string }): React.JSX.Element => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: 'text.secondary',
      fontSize: 11,
    }}
  >
    {title}
  </Typography>
);

export const RolesAdminPanel = ({
  role,
  availablePermissions,
  closeHandler,
  saveHandler,
}: Props): React.JSX.Element => {
  const isEdit = role !== undefined;

  const [name, setName] = useState<string>(role?.name ?? '');
  const [coordinate, setCoordinate] = useState<string>(role?.coordinate ?? '');
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(role?.permissions ?? []);
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const touch = (field: string): void => setTouchedFields((prev) => ({ ...prev, [field]: true }));

  useEffect(() => {
    setName(role?.name ?? '');
    setCoordinate(role?.coordinate ?? '');
    setSelectedPermissions(role?.permissions ?? []);
    setTouchedFields({});
  }, [role]);

  const payload = useMemo(
    (): Partial<Role> => ({
      ...role,
      name,
      coordinate,
      permissions: selectedPermissions,
    }),
    [role, name, coordinate, selectedPermissions],
  );

  const isValid = name !== '' && coordinate !== '' && selectedPermissions.length > 0;

  return (
    <Stack sx={{ height: '100%' }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AdminPanelSettingsOutlined color="action" />
          <Stack spacing={0}>
            <Typography variant="h6" fontWeight={700}>
              {isEdit ? 'Edit role' : 'Add role'}
            </Typography>
            {isEdit && (
              <Typography variant="caption" color="text.secondary">
                {role?.name}
              </Typography>
            )}
          </Stack>
        </Stack>
        <IconButton onClick={closeHandler} size="small" aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
        <Stack spacing={4}>
          {/* Identity */}
          <Stack spacing={1.5}>
            <SectionHeader title="Identity" />
            <StyledTextField
              label="Role name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => touch('name')}
              error={touchedFields.name && name === ''}
            />
            <CoordinateInput value={coordinate} onChange={setCoordinate} trailingDelimiter={false} />
          </Stack>

          {/* Permissions */}
          <Stack spacing={1.5}>
            <SectionHeader title="Permissions" />
            <Typography variant="caption" color="text.secondary">
              Select one or more permissions to grant this role.
            </Typography>
            <RolePermissionSelect
              width="100%"
              label="Permissions"
              role={payload}
              allPermissions={availablePermissions}
              saveHandler={(change) => setSelectedPermissions([...(change?.permissions ?? [])])}
              noDialog
            />
          </Stack>
        </Stack>
      </Box>

      {/* Footer */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button color="inherit" startIcon={<Close />} onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          variant="contained"
          endIcon={isEdit ? <Save /> : <Add />}
          onClick={() => saveHandler(payload)}
          disabled={!isValid}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </Stack>
    </Stack>
  );
};
